<script lang="ts" setup>
import { computed, defineComponent } from 'vue'

import { Background } from '@base/_types/Background'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import USAvatar from '@base/components/USAvatar/USAvatar.vue'
import USButton from '@base/components/USButton/USButton.vue'

import { useUserStore } from '@base/store/userStore'

import SplitContent from '@player/_components/SplitContent/SplitContent.vue'

import { coursesRouteNames } from '@player/courses/router/routeNames'
import { eventsRouteNames } from '@player/events/router/routeNames'

import blobBg from '@player/users/assets/register-step-5-blob-bg.svg'

defineComponent({
  name: 'RegisterStep5'
})

const userStore = useUserStore()

const generateInitials = computed(() => {
  return `${userStore.firstName.charAt(0).toUpperCase()}${userStore.lastName
    .charAt(1)
    .toUpperCase()}`
})

const bgImage: Background = {
  fullWidth: true,
  overflow: true,
  media: [
    {
      type: 'image',
      position: {
        alignHorizontal: 'left_50',
        alignVertical: 'center',
        centerAnchor: false,
        cover: false,
        foreground: false,
        objectPositionHorizontal: 'center',
        objectPositionVertical: 'center'
      },
      imageSet: {
        mobile: {
          fadeIn: true,
          lazy: true,
          src: blobBg
        }
      }
    }
  ]
}
</script>

<template>
  <div class="register-step-5">
    <BackgroundWrapper :background="bgImage" />
    <SplitContent
      :sticky="true"
      top-offset="var(--player-header-height)"
      class="margin-top--5 margin-bottom--5"
    >
      <template #left>
        <div class="margin-bottom--6">
          <h1 class="heading--h3 margin-bottom--8">
            Welcome to UpSwing,
            {{ userStore.membership.active ? 'Premium' : '' }}
            {{ userStore.firstName }}!
          </h1>
          <p class="body--p1--regular">
            It is now time to start earning rewards! Continue by exploring
            exclusive golf events or search for available tee times in your
            area:
          </p>
          <div class="register-step-5__buttons">
            <div class="register-step-5__button margin-right--7">
              <router-link :to="{ name: eventsRouteNames.Events }">
                <USButton label="Explore Events" icon="trophy" />
              </router-link>
            </div>
            <div class="register-step-5__button">
              <router-link :to="{ name: coursesRouteNames.Courses }">
                <USButton label="Search Courses" icon="golf-club" />
              </router-link>
            </div>
          </div>
        </div>
      </template>
      <template #right>
        <div class="register-step-5__right">
          <USAvatar
            :src="userStore.profilePicture"
            :initials="generateInitials"
            size="370px"
          />
        </div>
      </template>
    </SplitContent>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

.register-step-5 {
  color: var(--color--neutral-a1);

  &__buttons {
    align-items: center;
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    margin-top: var(--spacing--6);

    @include bp.min-width(bp.$breakpoint--9) {
      flex-flow: row nowrap;
    }
  }

  &__right {
    display: flex;
    margin-top: var(--spacing--5);
    justify-content: center;

    @include bp.min-width(bp.$breakpoint--7) {
      margin-top: 0;
    }
  }

  &__button {
    margin-bottom: var(--spacing--7);
  }
}

// :deep(.us-avatar) {
//   height: 370px;
//   width: 370px;
// }
:deep(.us-avatar__initials-label) {
  font-size: var(--font-size--z1);
}

:deep(.split-content__content) {
  flex-flow: column-reverse;

  @media (min-width: bp.$breakpoint--tablet) {
    flex-flow: row wrap;
  }
}
</style>
